import type { ReactNode } from 'react';
import { useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { useFeatures } from '@/shared/session/currentUser';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { formatBytes, formatDate } from '@/shared/utils/format';

import type { RetentionPreview, RetentionRun, StorageUsage } from './types';
import { plural, summary } from './retentionText';
import { useMeasureStorage, useRetention, useRunRetention, useStorageUsage } from './useAdmin';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'long' });
const shortDateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

/**
 * A scheduled run that has not left a report for longer than this is late: one night
 * missed plus the slack of a slow purge. The page says so instead of leaving the reader
 * to compare dates.
 */
const LATE_AFTER_MS = 26 * 60 * 60 * 1000;

/**
 * What the system keeps, for how long, and why (Anexo II §4.a: since the Adenda nº 2,
 * the emails while the service lasts), told to the person who
 * administers the shop, not to a developer: where the deleted copy lived, that the
 * mailbox itself is never touched, what stays, and what tonight's run would do.
 *
 * The figures are the API's `--dry-run`: what would go if the run happened this minute.
 * A count that grows day after day, or a last run older than a night, is the sign the
 * nightly run is failing, and the page says it in words.
 */
export function DataTab() {
  const retention = useRetention();
  const run = useRunRetention();
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<RetentionRun | null>(null);

  return (
    <div className="flex flex-col gap-4">
      {retention.isPending ? <Spinner variant="block" label="Cargando la retención" /> : null}
      {retention.isError ? (
        <ErrorMessage error={retention.error}>No se pudo cargar qué se borra</ErrorMessage>
      ) : null}
      {retention.data ? (
        <>
          <Explanation preview={retention.data} />

          <Card
            title="Qué se borraría hoy"
            aside={
              confirming ? (
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={run.isPending}
                    onClick={() =>
                      run.mutate(undefined, {
                        onSuccess: (result) => {
                          setDone(result);
                          setConfirming(false);
                        },
                      })
                    }
                  >
                    {run.isPending ? 'Borrando…' : 'Sí, borrar ahora'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
                    No
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
                  Ejecutar ahora
                </Button>
              )
            }
          >
            <Today preview={retention.data} />
            <p className="text-ink-3 text-[13px] leading-snug">
              {retention.data.scheduled
                ? 'No hace falta pulsar nada: «Ejecutar ahora» hace en este momento lo mismo que haría la pasada de la noche, y queda anotado en el registro de accesos.'
                : 'Lo que haga «Ejecutar ahora» queda anotado en el registro de accesos.'}
            </p>
          </Card>

          {run.isError ? (
            <p role="alert" className="text-danger text-[13.5px]">
              {errorMessage(run.error)}
            </p>
          ) : null}

          {done ? (
            // The run just made is the last run: one message, not the same figures twice.
            <Callout tone="ok" title="Hecho." role="status">
              {summary({
                bodies: done.bodies_purged,
                rawFiles: done.raw_files_deleted,
                attachments: done.attachments_deleted,
                accessLogRows: done.access_log_rows_deleted,
                guidelines: done.guidelines_deleted ?? 0,
              })}
              {done.raw_files_failed > 0
                ? ` ${done.raw_files_failed} ${plural(done.raw_files_failed, 'fichero no se pudo borrar', 'ficheros no se pudieron borrar')}: se reintenta mañana.`
                : ''}{' '}
              Queda anotado en el registro de accesos.
            </Callout>
          ) : (
            <LastRun preview={retention.data} />
          )}
        </>
      ) : null}
      {/* Not the retention's: it says what the bucket weighs even when that did not load. */}
      <StorageCard />
    </div>
  );
}

/**
 * Why the copy goes, where it goes from and what stays. Every number comes from the
 * API, so the text and the server never disagree about the days.
 *
 * Since contract v10.39 the emails are kept while the service lasts (Adenda nº 2) and
 * `body_retention_days` comes as null; a number only when Cubas asked for less. A back
 * before v10.39 sends no `guidelines_retention_days`: then it was the same clock.
 */
function Explanation({ preview }: { preview: RetentionPreview }) {
  const { document_archive: documentArchive } = useFeatures();
  const days = preview.body_retention_days;
  const learnedDays = preview.guidelines_retention_days ?? days;
  return (
    <Card title="Qué se borra, de dónde y por qué">
      <div className="text-ink-2 flex flex-col gap-2 text-[14px] leading-relaxed">
        {days === null ? (
          <p>
            Para clasificar un correo y enseñarlo en la bandeja, la aplicación guarda una copia de
            él en el servidor.{' '}
            <strong className="text-ink">
              Esa copia se guarda entera mientras dure el servicio
            </strong>
            : el texto, el fichero original y los adjuntos. Lo dice el contrato de protección de
            datos (Anexo II, tal como lo dejó la adenda de la segunda fase), porque hacen falta para
            trabajar con ellos.
          </p>
        ) : (
          <p>
            Para clasificar un correo y enseñarlo en la bandeja, la aplicación guarda una copia de
            él en el servidor. Esa copia no se guarda para siempre:{' '}
            <strong className="text-ink">
              a los {days} días de haber llegado, un correo ya clasificado pierde su contenido
            </strong>
            . Lo habéis pedido así: el contrato de protección de datos (Anexo II) permite guardarlo
            mientras dure el servicio.
          </p>
        )}
        <p>
          <strong className="text-ink">De dónde se borra:</strong> de la copia de la aplicación, en
          el servidor.{' '}
          {days === null
            ? null
            : 'El texto sale de la base de datos y el fichero original, del almacén de ficheros. '}
          <strong className="text-ink">Vuestro buzón de correo no se toca</strong>: el mensaje sigue
          en él tal como llegó.
        </p>
      </div>
      <div className="grid gap-4 text-[13.5px] leading-snug sm:grid-cols-2">
        <List title="Se borra" tone="danger">
          {days === null ? null : (
            <>
              <li>El texto del correo, el que se lee en la bandeja.</li>
              <li>
                El fichero original completo (el mensaje tal cual llegó), del almacén de ficheros.
              </li>
              {documentArchive ? null : (
                <li>Los adjuntos, salvo los de un correo del que salió un presupuesto.</li>
              )}
            </>
          )}
          <li>
            Las anotaciones del registro de accesos con más de {preview.access_log_retention_days}{' '}
            días.
          </li>
          <li>
            Lo aprendido de las notas que ya no vale (lo de un solo correo, lo sustituido y lo
            desactivado), a los {learnedDays} días.
          </li>
        </List>
        <List title="Se queda" tone="ok">
          {days === null ? (
            <li>Cada correo entero: el texto, el fichero original y los adjuntos.</li>
          ) : (
            <li>El asunto, el remitente y la fecha.</li>
          )}
          <li>La categoría, quién la confirmó y qué se hizo con el correo.</li>
          <li>Las correcciones con sus notas, y lo aprendido que sigue vigente.</li>
          {days === null ? null : (
            <li>
              Un correo pendiente de clasificar o con error: no se toca hasta que se resuelve.
            </li>
          )}
          {documentArchive ? (
            <li>Los adjuntos archivados: siguen su propio plazo, en «Documentos».</li>
          ) : null}
        </List>
      </div>
      <p className="text-ink-3 text-[13px] leading-snug">
        {preview.scheduled
          ? `Cuándo: cada noche a las ${preview.run_at}, sola.`
          : 'La pasada automática está apagada: solo borra «Ejecutar ahora».'}
      </p>
    </Card>
  );
}

function List({
  title,
  tone,
  children,
}: {
  title: string;
  tone: 'danger' | 'ok';
  children: ReactNode;
}) {
  const colour = tone === 'danger' ? 'text-danger' : 'text-ok';
  return (
    <div>
      <p className={`text-[12px] font-semibold tracking-[0.1em] uppercase ${colour}`}>{title}</p>
      <ul className="text-ink-2 mt-1.5 flex list-disc flex-col gap-1 pl-5">{children}</ul>
    </div>
  );
}

/** What tonight's run would take, in one sentence, with the cutoff date it applies. */
function Today({ preview }: { preview: RetentionPreview }) {
  const total =
    preview.bodies_to_purge +
    preview.raw_files_to_delete +
    preview.attachments_to_delete +
    preview.access_log_rows_to_delete +
    (preview.guidelines_to_delete ?? 0);
  if (total === 0) {
    return (
      <p className="text-ok text-[14px] font-semibold" role="status">
        {preview.body_retention_days === null
          ? 'Hoy no hay nada que borrar.'
          : `Hoy no hay nada que borrar: ningún correo clasificado tiene más de ${preview.body_retention_days} días.`}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-[14px]" role="status">
      <p className="text-ink font-semibold">
        {summary({
          bodies: preview.bodies_to_purge,
          rawFiles: preview.raw_files_to_delete,
          attachments: preview.attachments_to_delete,
          accessLogRows: preview.access_log_rows_to_delete,
          guidelines: preview.guidelines_to_delete ?? 0,
          future: preview.scheduled ? 'tonight' : 'now',
        })}
      </p>
      {preview.bodies_to_purge > 0 && preview.cutoff ? (
        <p className="text-ink-3 text-[13px] leading-snug">
          Son los correos recibidos antes del {formatDate(dateFormat, preview.cutoff)}.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The last run, in one line, and a warning when a scheduled run has not happened when
 * it should have. `finished_at` is whatever the worker left in Redis, so nothing here
 * trusts its shape.
 */
function LastRun({ preview }: { preview: RetentionPreview }) {
  const last = preview.last_run;
  if (!last) {
    return (
      <Callout compact tone="info" title="Todavía no ha corrido ninguna pasada." role="status">
        {preview.scheduled
          ? `La primera será esta noche a las ${preview.run_at}; si mañana sigue sin aparecer aquí, no se está haciendo.`
          : null}
      </Callout>
    );
  }
  const finished = typeof last.finished_at === 'string' ? last.finished_at : null;
  const finishedMs = finished ? new Date(finished).getTime() : Number.NaN;
  const late =
    preview.scheduled && !Number.isNaN(finishedMs) && Date.now() - finishedMs > LATE_AFTER_MS;
  const when = finished
    ? `el ${formatDate(shortDateFormat, finished)} a las ${formatDate(timeFormat, finished)}`
    : 'sin terminar';
  const text = `${when}. ${summary({
    bodies: number(last.bodies_purged),
    rawFiles: number(last.raw_files_deleted),
    attachments: number(last.attachments_deleted),
    accessLogRows: number(last.access_log_rows_deleted),
    guidelines: number(last.guidelines_deleted),
  })}`;
  const failed = number(last.raw_files_failed);
  return (
    <div className="flex flex-col gap-2">
      <Callout compact tone="neutral" title="Última pasada:" role="status">
        {text}
        {failed > 0
          ? ` ${failed} ${plural(failed, 'fichero no se pudo borrar', 'ficheros no se pudieron borrar')}: se reintenta en la siguiente.`
          : ''}
      </Callout>
      {late ? (
        <Callout compact tone="warn" title="La pasada de anoche no ha corrido.">
          Tenía que haber corrido a las {preview.run_at}. Si mañana sigue igual, avisa a quien
          administra la aplicación.
        </Callout>
      ) : null}
    </div>
  );
}

function number(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

/** The roots of the bucket, by what a person calls them (`cubas.core.storage` of the back). */
const FOLDER_LABELS: Record<string, string> = {
  correos: 'Correos y sus adjuntos',
  presupuestos: 'PDF de los presupuestos',
  enviados: 'Adjuntos de lo enviado',
  catalogos: 'Catálogos',
  tarifas: 'Tarifas',
  documentos: 'Archivo',
};

const percentFormat = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  maximumFractionDigits: 1,
});

/**
 * What the bucket weighs against the terabyte of the Adenda nº 2 (cl. 5.3), measured every
 * night after the retention (contract v10.49). From 80 % the back leaves a warning in
 * Incidencias; here it is a line in amber. «Medir ahora» walks the whole bucket.
 */
function StorageCard() {
  const usage = useStorageUsage();
  const measure = useMeasureStorage();
  return (
    <Card
      title="Lo que ocupa el almacén"
      aside={
        <Button
          size="sm"
          variant="secondary"
          disabled={measure.isPending || usage.isPending}
          onClick={() => measure.mutate()}
        >
          {measure.isPending ? 'Midiendo…' : 'Medir ahora'}
        </Button>
      }
    >
      {usage.isPending ? <Spinner label="Cargando lo que ocupa" /> : null}
      {usage.isError ? (
        <ErrorMessage error={usage.error}>No se pudo cargar lo que ocupa el almacén</ErrorMessage>
      ) : null}
      {usage.data ? <StorageFigures usage={usage.data} /> : null}
      {measure.isError ? <ErrorMessage error={measure.error}>No se pudo medir</ErrorMessage> : null}
    </Card>
  );
}

function StorageFigures({ usage }: { usage: StorageUsage }) {
  if (usage.total_bytes === null || usage.total_bytes === undefined || !usage.measured_at) {
    return (
      <p className="text-ink-2 text-[14px]">
        Todavía no se ha medido. Se mide cada noche, después de la retención; «Medir ahora» lo hace
        en este momento.
      </p>
    );
  }
  const share = usage.used_share ?? usage.total_bytes / usage.quota_bytes;
  const when = new Date(usage.measured_at);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-ink text-[14px]">
        <strong className="tabular-nums">{formatBytes(usage.total_bytes)}</strong> de{' '}
        <span className="tabular-nums">{formatBytes(usage.quota_bytes)}</span> (
        {percentFormat.format(share)}) · medido el {formatDate(shortDateFormat, usage.measured_at)}{' '}
        a las {timeFormat.format(when)}
      </p>
      <div
        role="meter"
        aria-label="Espacio usado"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share * 100)}
        className="bg-paper-2 h-2 overflow-hidden rounded-full"
      >
        <div
          className={usage.warning ? 'bg-warn h-full' : 'bg-accent h-full'}
          style={{ width: `${Math.min(100, Math.round(share * 100))}%` }}
        />
      </div>
      {usage.warning ? (
        <Callout tone="warn" compact>
          Pasa del 80 % del espacio contratado. Hay que ampliarlo o borrar lo que sobre.
        </Callout>
      ) : null}
      {(usage.folders ?? []).length > 0 ? (
        <ul className="text-ink-2 flex flex-col gap-1 text-[13.5px]">
          {(usage.folders ?? []).map((folder) => (
            <li key={folder.folder} className="flex justify-between gap-4">
              <span>{FOLDER_LABELS[folder.folder] ?? folder.folder}</span>
              <span className="tabular-nums">
                {formatBytes(folder.bytes)} ·{' '}
                {plural(folder.objects, `${folder.objects} fichero`, `${folder.objects} ficheros`)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
